/**
 * SubmissionIssuesTab — top-level, submission-wide list of every gap /
 * regulatory issue across the run, grouped by section in submission order.
 *
 * The section Inspector already has a per-section Issues tab (DataGapsTabPanel);
 * this is its run-wide sibling: one place to see all open + resolved + dismissed
 * + stale issues at once, ordered the way the document reads. Rows reuse the
 * shared ``IssueRow`` component so the two views stay in sync — same status
 * chips, gap/regulatory styling, regulatory "to resolve" guidance, and per-row
 * actions (jump-to-marker in Word, Mark N/A, Reopen, Resolve with asthra).
 *
 * Data: GET /runs/{runId}/issues?status=all (see ``getRunIssues``). The server
 * orders sections by ``scoped_module_ids`` and enriches regulatory rows; this
 * component only re-sorts within a section into status bands (open first).
 *
 * Fresh check on open: after the rollup loads, every section that still has an
 * OPEN issue is reconciled against its OWN live Word text
 * (``…/issues/reconcile``), so a flag whose marker the writer deleted (but hasn't
 * Saved) auto-resolves the moment the tab opens — no Refresh button. The check is
 * section-scoped (an identical marker in another section can't keep this one
 * open) and guarded: a blank/absent live read, or the live doc still loading,
 * skips the reconcile so it can never falsely resolve every flag; outside Word
 * (web/guru) the server's own sweep still settles absent markers against the SSOT.
 */

import { makeStyles, Spinner } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
	getRunAuditLibrary,
	getRunIssues,
	patchSectionIssue,
	reconcileSectionIssues,
	type RunAuditLibrary,
	type RunIssuesResponse,
	type SectionIssue,
	startRunAuditLibrary,
} from "../../api/client";
import { isLiveDocLoading } from "../../services/liveDocLoadState";
import { navigateToSection, readAllSectionBodies, selectTextMarker } from "../../services/wordWriter";
import { getAuditLibraryBuild, getEntry, reviewKey, subscribeAuditLibraryBuild } from "../reviewStore";
import { AuditLibraryPanel } from "./inspector/AuditLibraryPanel";
import { IssueRow, severityRank, STATUS_ORDER } from "./inspector/IssueRow";
import { ProposalReviewPanel } from "./ProposalReviewPanel";
import { fonts, inkColors, surfaceColors } from "../tokens";

/** A section + the specific open issues selected to bulk-resolve. Carries the
 *  section number/title for the wizard's review step (stripped before POST). */
export interface ResolveSelectionTarget {
	module_id: string;
	number: string;
	title: string;
	issue_ids: string[];
}

interface SubmissionIssuesTabProps {
	/** Active run id. Null before a run exists — the tab shows an empty hint. */
	runId: string | null;
	/** Host-wired "resolve with asthra" — seeds a chat turn scoped to the issue's
	 *  section with the marker text. Button hidden when absent. */
	onResolveWithAsthra?: (issue: SectionIssue) => void;
	/** Reports the current open-issue count up to the shell so the Issues-tab
	 *  badge stays live after an in-tab dismiss/reopen (no shell refetch). Fired
	 *  on load and after every successful patch. */
	onOpenCountChange?: (openCount: number) => void;
	/** Host-wired bulk resolve: opens the resolve wizard for the selected open
	 *  issues (grouped by section). Selection UI is hidden when absent. */
	onResolveSelected?: (targets: ResolveSelectionTarget[]) => void;
	/** External re-fetch trigger. The shell bumps it after a Save (the server
	 *  reconciled the ledger inside ``bulk-edit`` — writer-deleted markers →
	 *  resolved) and after an accepted chat edit, so the list reflects the new
	 *  state without the writer pressing Refresh. The initial mount fetch is
	 *  ``load``'s own effect; this one skips its first (mount) value. */
	issuesRefreshSignal?: number;
	/** Re-fetch trigger for the "Proposals to review" band — the shell bumps it
	 *  when a resolve pass produces a proposal / finishes. */
	proposalsRefreshSignal?: number;
	/** Active source library — scopes a Revise rewrite's retrieval. */
	libraryId?: string | null;
	/** Fired after a proposal is approved/rejected so the shell can refresh the
	 *  Issues list (an approve resolved the issue; a reject left it open). */
	onProposalApplied?: () => void;
}

const useStyles = makeStyles({
	root: {
		height: "100%",
		overflowY: "auto",
		padding: "10px 12px 24px",
		boxSizing: "border-box",
	},
	loading: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink4,
	},
	error: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: "#B71C1C",
		margin: "4px 0",
	},
	empty: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink4,
		fontStyle: "italic",
		padding: "8px 2px",
	},
	// Sticky summary bar of status tallies.
	summary: {
		position: "sticky",
		top: 0,
		zIndex: 1,
		display: "flex",
		flexDirection: "column",
		gap: "6px",
		background: surfaceColors.paper,
		borderBottom: `1px solid ${surfaceColors.line}`,
		padding: "6px 2px 8px",
		marginBottom: "10px",
	},
	summaryRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: "10px",
		flexWrap: "wrap",
	},
	counts: {
		display: "flex",
		alignItems: "center",
		gap: "10px",
		flexWrap: "wrap",
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink3,
	},
	countItem: { display: "inline-flex", alignItems: "center", gap: "4px" },
	countNum: { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
	countOpen: { color: "#B71C1C" },
	section: { marginBottom: "16px" },
	sectionHeader: {
		display: "flex",
		alignItems: "center",
		gap: "6px",
		marginBottom: "6px",
		paddingBottom: "3px",
		borderBottom: `1px solid ${surfaceColors.line}`,
		cursor: "pointer",
		userSelect: "none",
		":hover": { background: "#F7F7FA" },
	},
	chevron: {
		fontSize: "9px",
		color: inkColors.ink4,
		width: "10px",
		flex: "0 0 auto",
		textAlign: "center",
	},
	sectionNumber: {
		fontFamily: fonts.mono,
		fontSize: "11px",
		fontWeight: 700,
		color: inkColors.ink2,
		fontVariantNumeric: "tabular-nums",
	},
	sectionTitle: {
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink3,
		flex: "1 1 auto",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	sectionOpen: {
		fontFamily: fonts.body,
		fontSize: "9.5px",
		fontWeight: 700,
		color: "#B71C1C",
		flex: "0 0 auto",
	},
	list: { display: "flex", flexDirection: "column", gap: "6px" },
	// Bulk-resolve selection toolbar (shown when onResolveSelected is wired).
	selectAll: {
		display: "inline-flex",
		alignItems: "center",
		gap: "5px",
		fontFamily: fonts.body,
		fontSize: "11px",
		color: inkColors.ink2,
		cursor: "pointer",
	},
	checkbox: { cursor: "pointer", accentColor: "#1565C0" },
	selCount: { fontFamily: fonts.body, fontSize: "11px", color: inkColors.ink3 },
	resolveBtn: {
		marginLeft: "auto",
		border: "none",
		borderRadius: "5px",
		background: "#1565C0",
		color: "#FFFFFF",
		fontFamily: fonts.body,
		fontSize: "11px",
		fontWeight: 700,
		padding: "5px 12px",
		cursor: "pointer",
		":hover": { background: "#0F4C9A" },
		":disabled": { background: "#B7C4D6", cursor: "default" },
	},
	sectionSelect: { cursor: "pointer", accentColor: "#1565C0", flex: "0 0 auto" },
});

/** Re-tally status counts from the (possibly patched) sections. */
function tallyStatusCounts(sections: RunIssuesResponse["sections"]): RunIssuesResponse["status_counts"] {
	const counts = { open: 0, resolved: 0, dismissed: 0, stale: 0, total: 0 };
	for (const section of sections) {
		for (const issue of section.issues) {
			counts.total += 1;
			counts[issue.status] += 1;
		}
	}
	return counts;
}

/** Re-sort each section into status bands (open first), then chronological. */
function withSortedBands(resp: RunIssuesResponse): RunIssuesResponse {
	return {
		...resp,
		sections: resp.sections.map((s) => ({
			...s,
			issues: [...s.issues].sort(
				(a, b) =>
					STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
					severityRank(a.severity) - severityRank(b.severity) ||
					(a.first_seen_at ?? "").localeCompare(b.first_seen_at ?? ""),
			),
		})),
	};
}

/**
 * Fresh check against the live Word document: for every section that still has an
 * OPEN issue, read its own live text and reconcile the ledger against it so
 * markers the writer deleted auto-resolve. Section-scoped and best-effort — any
 * failure (or a blank/absent read, or the doc still loading) leaves that
 * section's rollup untouched, so it can never falsely resolve every flag. Returns
 * a payload with the reconciled sections spliced in (counts re-tallied).
 */
async function reconcileAgainstLiveDoc(runId: string, resp: RunIssuesResponse): Promise<RunIssuesResponse> {
	if (isLiveDocLoading()) return resp;
	const openSections = resp.sections.filter((s) => s.issues.some((i) => i.status === "open"));
	if (openSections.length === 0) return resp;

	let bodies: Map<string, string | null>;
	try {
		bodies = await readAllSectionBodies(openSections.map((s) => s.number));
	} catch {
		return resp; // not in Word / read failed — keep the server rollup
	}

	const reconciledById = new Map<string, SectionIssue[]>();
	await Promise.all(
		openSections.map(async (s) => {
			// Never reconcile a section whose regulatory audit is running: its live
			// doc is intentionally stripped of [REGULATOR FLAG:…] markers until the
			// audit completes and re-projects them (see reviewStore ``startAudit``).
			// Reconciling mid-audit — this runs on the ``audit_complete``
			// issuesRefreshSignal bump too — would resolve every fresh flag as
			// ``writer_manual``. Leave the server rollup for those; the audit's own
			// completion path refreshes them once the flags are back.
			if (getEntry(reviewKey(runId, s.module_id)).runState === "running") return;
			const liveText = bodies.get(s.number);
			// Guard: never reconcile on a blank/absent read — that would resolve
			// every open flag in the section.
			if (!liveText || !liveText.trim()) return;
			try {
				const r = await reconcileSectionIssues(runId, s.module_id, liveText);
				reconciledById.set(s.module_id, r.issues);
			} catch {
				/* leave this section as-is */
			}
		}),
	);
	if (reconciledById.size === 0) return resp;

	const sections = resp.sections.map((s) => {
		const next = reconciledById.get(s.module_id);
		if (!next) return s;
		return { ...s, issues: next, open_count: next.filter((i) => i.status === "open").length };
	});
	return { ...resp, sections, status_counts: tallyStatusCounts(sections) };
}

export const SubmissionIssuesTab: React.FC<SubmissionIssuesTabProps> = ({
	runId,
	onResolveWithAsthra,
	onOpenCountChange,
	onResolveSelected,
	issuesRefreshSignal,
	proposalsRefreshSignal,
	libraryId,
	onProposalApplied,
}) => {
	const styles = useStyles();
	const [payload, setPayload] = useState<RunIssuesResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	// Bulk-resolve selection — a flat set of open issue ids (globally unique).
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	// Accordion: module_ids whose issue list is expanded (collapsed by default).
	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	// The run's dynamic audit library (precedent-aware audit) — the provenance
	// behind the run's regulatory findings, shown atop the run-wide issues. Null
	// until fetched; the panel self-hides when the feature is off or the run has
	// none, so it is safe to render unconditionally.
	const [auditLib, setAuditLib] = useState<RunAuditLibrary | null>(null);
	const requestKeyRef = useRef<string>("");

	const loadAuditLibrary = useCallback(() => {
		if (!runId) {
			setAuditLib(null);
			return;
		}
		getRunAuditLibrary(runId)
			.then(setAuditLib)
			.catch(() => setAuditLib(null));
	}, [runId]);

	// Live audit-library build phase (SSE-driven) — narrates the build between the
	// 6s status polls and surfaces the custom-submission temp-library bootstrap the
	// poll can miss entirely (no documents yet). See DataGapsTabPanel for the twin.
	const auditBuildPhase = useSyncExternalStore(subscribeAuditLibraryBuild, () =>
		runId ? getAuditLibraryBuild(runId) : null,
	);
	const liveBuildMessage = auditBuildPhase?.message ?? null;

	// Manual (re)build of the run's audit library, with an optional steering note.
	// ``settingUpAudit`` is a latch held for the WHOLE (re)build (not just the POST),
	// released only once a poll sees the build reach a NEW terminal state — see the
	// matching note in DataGapsTabPanel. Clearing it at the 202 let polling stop
	// while the backend kept building, so the panel never advanced.
	const [settingUpAudit, setSettingUpAudit] = useState(false);
	const [auditBuildSeen, setAuditBuildSeen] = useState(false);
	const [auditSetupError, setAuditSetupError] = useState<string | null>(null);
	const rebuildTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const clearRebuildTimer = useCallback(() => {
		if (rebuildTimer.current) {
			clearTimeout(rebuildTimer.current);
			rebuildTimer.current = null;
		}
	}, []);
	const handleSetupAuditLibrary = useCallback(
		(steering: string) => {
			if (!runId) return;
			setAuditSetupError(null);
			setAuditBuildSeen(false);
			setSettingUpAudit(true);
			clearRebuildTimer();
			rebuildTimer.current = setTimeout(() => setSettingUpAudit(false), 5 * 60 * 1000);
			void startRunAuditLibrary(runId, steering)
				.then(() => loadAuditLibrary())
				.catch((err) => {
					setAuditSetupError(err instanceof Error ? err.message : String(err));
					setSettingUpAudit(false);
					clearRebuildTimer();
				});
		},
		[runId, loadAuditLibrary, clearRebuildTimer],
	);

	// Poll while a build is in flight (or the rebuild latch is held) so the panel
	// advances to ready on its own.
	const auditStatus = auditLib?.audit_library?.status;
	const auditPolling =
		settingUpAudit || auditStatus === "acquiring" || auditStatus === "processing" || !!auditBuildPhase;
	useEffect(() => {
		if (!auditPolling) return;
		const t = setInterval(loadAuditLibrary, 6000);
		return () => clearInterval(t);
	}, [auditPolling, loadAuditLibrary]);

	// Release the latch once the build has started and then reached a terminal state.
	useEffect(() => {
		if (!settingUpAudit) return;
		if (auditStatus === "acquiring" || auditStatus === "processing") {
			if (!auditBuildSeen) setAuditBuildSeen(true);
			return;
		}
		if (auditBuildSeen && (auditStatus === "ready" || auditStatus === "empty" || auditStatus === "failed")) {
			setSettingUpAudit(false);
			clearRebuildTimer();
		}
	}, [settingUpAudit, auditBuildSeen, auditStatus, clearRebuildTimer]);

	useEffect(() => clearRebuildTimer, [clearRebuildTimer]);

	const load = useCallback(async () => {
		if (!runId) {
			setLoading(false);
			setPayload(null);
			setError(null);
			return;
		}
		const key = runId;
		requestKeyRef.current = key;
		setLoading(true);
		setError(null);
		setActionError(null);
		setSelectedIds(new Set());
		try {
			const resp = await getRunIssues(runId, "all");
			if (requestKeyRef.current !== key) return;
			// Fresh check on open: reconcile each still-open section against its own
			// live Word text so writer-deleted markers auto-resolve (section-scoped,
			// guarded, best-effort). Then re-sort into status bands (open first).
			const reconciled = await reconcileAgainstLiveDoc(runId, resp);
			if (requestKeyRef.current !== key) return;
			setPayload(withSortedBands(reconciled));
		} catch (err) {
			if (requestKeyRef.current !== key) return;
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			if (requestKeyRef.current === key) setLoading(false);
		}
	}, [runId]);

	useEffect(() => {
		void load();
	}, [load]);

	// Re-fetch when the shell signals a ledger change (post-Save / accepted edit).
	// The mount value is skipped — the effect above already fetched — so a fresh
	// mount never double-fetches. Same guard pattern as DraftsPanel/HomeScene.
	const refreshSignalSeenRef = useRef(false);
	// biome-ignore lint/correctness/useExhaustiveDependencies: issuesRefreshSignal is the external re-fetch trigger
	useEffect(() => {
		if (!refreshSignalSeenRef.current) {
			refreshSignalSeenRef.current = true;
			return;
		}
		void load();
	}, [issuesRefreshSignal, load]);

	// Fetch the audit library on mount / run change, and re-fetch on the same
	// ledger-change signal the issues list uses (a run that just finished may have
	// only now finished acquiring precedent). Self-hiding, so no gating needed.
	// biome-ignore lint/correctness/useExhaustiveDependencies: issuesRefreshSignal is the re-fetch trigger
	useEffect(() => {
		loadAuditLibrary();
	}, [loadAuditLibrary, issuesRefreshSignal]);

	// Report the open count up to the shell whenever the payload changes (load or
	// in-tab patch) so the Issues-tab badge stays live without a shell refetch.
	useEffect(() => {
		if (payload) onOpenCountChange?.(payload.status_counts.open);
	}, [payload, onOpenCountChange]);

	// Jump to the marker in the live Word document; fall back to the section
	// heading when the exact marker text isn't found. Mirrors DataGapsTabPanel.
	const jumpToMarker = useCallback((issue: SectionIssue, sectionNumber: string) => {
		void selectTextMarker(issue.marker_text)
			.then((found) => {
				if (!found && sectionNumber) return navigateToSection(sectionNumber);
				return undefined;
			})
			.catch(() => {});
	}, []);

	const handlePatch = useCallback(
		async (issue: SectionIssue, status: "dismissed" | "open") => {
			setActionError(null);
			try {
				const updated = await patchSectionIssue(runId ?? "", issue.id, status);
				// A dismissed/reopened issue that is no longer open leaves the
				// selectable set — drop it from the bulk-resolve selection.
				if (updated.status !== "open") {
					setSelectedIds((prev) => {
						if (!prev.has(updated.id)) return prev;
						const next = new Set(prev);
						next.delete(updated.id);
						return next;
					});
				}
				setPayload((prev) => {
					if (!prev) return prev;
					const sections = prev.sections.map((s) =>
						s.module_id === updated.module_id
							? {
									...s,
									issues: s.issues.map((i) => (i.id === updated.id ? updated : i)),
									open_count: s.issues.reduce(
										(n, i) => n + ((i.id === updated.id ? updated.status : i.status) === "open" ? 1 : 0),
										0,
									),
								}
							: s,
					);
					return { ...prev, sections, status_counts: tallyStatusCounts(sections) };
				});
			} catch (err) {
				setActionError(err instanceof Error ? err.message : String(err));
			}
		},
		[runId],
	);

	// ── Bulk-resolve selection ────────────────────────────────────────────
	const selectionEnabled = Boolean(onResolveSelected);
	const allOpenIds = (payload?.sections ?? []).flatMap((s) =>
		s.issues.filter((i) => i.status === "open").map((i) => i.id),
	);
	const allSelected = allOpenIds.length > 0 && allOpenIds.every((id) => selectedIds.has(id));

	const toggleIssue = useCallback((issue: SectionIssue) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(issue.id)) next.delete(issue.id);
			else next.add(issue.id);
			return next;
		});
	}, []);

	const toggleSection = useCallback((openIdsInSection: string[]) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			const allOn = openIdsInSection.length > 0 && openIdsInSection.every((id) => next.has(id));
			for (const id of openIdsInSection) {
				if (allOn) next.delete(id);
				else next.add(id);
			}
			return next;
		});
	}, []);

	const toggleAll = useCallback(() => {
		setSelectedIds((prev) => (allOpenIds.every((id) => prev.has(id)) ? new Set() : new Set(allOpenIds)));
	}, [allOpenIds]);

	const toggleExpanded = useCallback((moduleId: string) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(moduleId)) next.delete(moduleId);
			else next.add(moduleId);
			return next;
		});
	}, []);

	const startResolve = useCallback(() => {
		if (!onResolveSelected || !payload) return;
		const targets: ResolveSelectionTarget[] = payload.sections
			.map((s) => ({
				module_id: s.module_id,
				number: s.number,
				title: s.title,
				issue_ids: s.issues.filter((i) => i.status === "open" && selectedIds.has(i.id)).map((i) => i.id),
			}))
			.filter((t) => t.issue_ids.length > 0);
		if (targets.length > 0) onResolveSelected(targets);
	}, [onResolveSelected, payload, selectedIds]);

	// The review queue band — shown above the issues whenever a resolve pass has
	// produced pending proposals. Rendered in every runId branch so the writer can
	// review proposals even while the issues list is loading or errored.
	const reviewPanel = runId ? (
		<ProposalReviewPanel
			runId={runId}
			refreshSignal={proposalsRefreshSignal}
			libraryId={libraryId}
			onDecided={() => {
				void load();
				onProposalApplied?.();
			}}
			onError={setActionError}
		/>
	) : null;

	// The run's audit library (precedent-aware audit). Rendered in every runId
	// branch so it stays visible while the issues list loads or errors; the panel
	// self-hides when the feature is off or the run has no audit library.
	const auditPanel = runId ? (
		<AuditLibraryPanel
			data={auditLib}
			onRefresh={loadAuditLibrary}
			hideWhenNotReady
			allowSetup
			onSetup={handleSetupAuditLibrary}
			settingUp={settingUpAudit}
			setupError={auditSetupError ?? undefined}
			liveBuildMessage={liveBuildMessage}
		/>
	) : null;

	if (!runId) {
		return (
			<div className={styles.root}>
				<div className={styles.empty}>No run yet — start or load a draft to see its issues.</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className={styles.root}>
				{reviewPanel}
				{auditPanel}
				<div className={styles.loading}>
					<Spinner size="extra-tiny" />
					<span>Collecting issues across the submission…</span>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={styles.root}>
				{reviewPanel}
				{auditPanel}
				<div className={styles.error}>Couldn&apos;t load the submission&apos;s issues: {error}</div>
			</div>
		);
	}

	const counts = payload?.status_counts ?? { open: 0, resolved: 0, dismissed: 0, stale: 0, total: 0 };
	const sectionsWithIssues = (payload?.sections ?? []).filter((s) => s.issues.length > 0);

	return (
		<div className={styles.root}>
			{reviewPanel}
			{auditPanel}
			<div className={styles.summary}>
				<div className={styles.summaryRow}>
					<div className={styles.counts}>
						<span className={styles.countItem}>
							<span className={`${styles.countNum} ${styles.countOpen}`}>{counts.open}</span> open
						</span>
						<span className={styles.countItem}>
							<span className={styles.countNum}>{counts.resolved}</span> resolved
						</span>
						<span className={styles.countItem}>
							<span className={styles.countNum}>{counts.dismissed}</span> dismissed
						</span>
						<span className={styles.countItem}>
							<span className={styles.countNum}>{counts.stale}</span> stale
						</span>
						<span className={styles.countItem}>
							<span className={styles.countNum}>{counts.total}</span> total
						</span>
					</div>
				</div>

				{selectionEnabled && allOpenIds.length > 0 ? (
					<div className={styles.summaryRow}>
						<label className={styles.selectAll}>
							<input
								type="checkbox"
								className={styles.checkbox}
								checked={allSelected}
								onChange={toggleAll}
								aria-label="Select all open issues"
							/>
							Select all
						</label>
						<span className={styles.selCount}>{selectedIds.size} selected</span>
						<button
							type="button"
							className={styles.resolveBtn}
							disabled={selectedIds.size === 0}
							onClick={startResolve}
							title="Resolve the selected issues with asthra"
						>
							Resolve {selectedIds.size} with asthra
						</button>
					</div>
				) : null}
			</div>

			{actionError ? <div className={styles.error}>{actionError}</div> : null}

			{sectionsWithIssues.length === 0 ? (
				<div className={styles.empty}>No issues across the submission. ✓</div>
			) : (
				sectionsWithIssues.map((section) => {
					const sectionOpenIds = section.issues.filter((i) => i.status === "open").map((i) => i.id);
					const sectionAllSelected =
						sectionOpenIds.length > 0 && sectionOpenIds.every((id) => selectedIds.has(id));
					const isExpanded = expanded.has(section.module_id);
					return (
						<div key={section.module_id} className={styles.section}>
							<div
								className={styles.sectionHeader}
								role="button"
								tabIndex={0}
								aria-expanded={isExpanded}
								onClick={() => toggleExpanded(section.module_id)}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										toggleExpanded(section.module_id);
									}
								}}
							>
								<span className={styles.chevron}>{isExpanded ? "▾" : "▸"}</span>
								{selectionEnabled && sectionOpenIds.length > 0 ? (
									<input
										type="checkbox"
										className={styles.sectionSelect}
										checked={sectionAllSelected}
										onClick={(e) => e.stopPropagation()}
										onChange={() => toggleSection(sectionOpenIds)}
										aria-label={`Select all open issues in ${section.number}`}
									/>
								) : null}
								<span className={styles.sectionNumber}>{section.number}</span>
								<span className={styles.sectionTitle}>{section.title}</span>
								{section.open_count > 0 ? (
									<span className={styles.sectionOpen}>{section.open_count} open</span>
								) : null}
							</div>
							{isExpanded ? (
								<div className={styles.list}>
									{section.issues.map((issue) => (
										<IssueRow
											key={issue.id}
											issue={issue}
											onJump={(i) => jumpToMarker(i, section.number)}
											onDismiss={(i) => void handlePatch(i, "dismissed")}
											onReopen={(i) => void handlePatch(i, "open")}
											onResolveWithAsthra={onResolveWithAsthra}
											selectable={selectionEnabled}
											selected={selectedIds.has(issue.id)}
											onToggleSelect={toggleIssue}
										/>
									))}
								</div>
							) : null}
						</div>
					);
				})
			)}
		</div>
	);
};

export default SubmissionIssuesTab;
